import { useMemo } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { TicketQueue, DEFAULT_STATUS, toCounts } from '../tickets/TicketQueue'
import { DATA_QUALITY_CATEGORY } from '../steering/steering-categories'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * Data quality — the same ticket queue as Steering, narrowed to the
 * `data_quality` category, living in the Data section (spec 2026-08-24-03,
 * lot 10).
 *
 * It is here and not in Steering because the work is different in kind: a
 * conflicting value, an unreadable scan or a duplicate document is data
 * stewardship, not asset management. Nobody calls a provider about it. Putting
 * it beside import and export puts it next to the other things one does *to*
 * the dataset rather than to the buildings.
 *
 * Its three subcategories — completeness, consistency, document hygiene — come
 * from the same server-side axis the steering pills use, so the two pages can
 * never disagree about where a ticket belongs.
 */
export function DataQualityPage() {
    const { org } = useParams({ from: '/$org/data/quality' })
    const search = useSearch({ from: '/$org/data/quality' })
    const navigate = useNavigate({ from: '/$org/data/quality' })
    const { t } = useTranslation(['data', 'issues'])

    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('data:breadcrumb'), path: `/${org}/data` },
        { title: t('data:quality.title') },
    ])

    const statusFilter = search.status ?? DEFAULT_STATUS
    const sort = search.sort === 'due' || search.sort === 'recent' ? search.sort : 'priority'
    const page = Math.max(1, Number(search.page) || 1)
    const selected = search.selected ?? null

    const setSearch = (next: Record<string, string | number | undefined>) => {
        const resetsPage = Object.keys(next).some((k) => k !== 'page' && k !== 'selected')
        navigate({
            search: (prev: Record<string, unknown>) => ({ ...prev, ...(resetsPage ? { page: undefined } : {}), ...next }) as never,
            replace: true,
        })
    }
    const select = (uid: string | null) => {
        navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, selected: uid ?? undefined }) as never, replace: false })
    }

    // One number, for the "All" pill and the header count.
    const categoryStats = $api.useQuery('get', '/api/v1/organizations/{orgCode}/issues/stats', {
        params: {
            path: { orgCode: org },
            query: { group_by: 'category', status: statusFilter, category: DATA_QUALITY_CATEGORY, assignee_uid: 'all', roots_only: search.children !== 1, follow_up: search.follow_up },
        },
    })
    const total = useMemo(
        () => toCounts(categoryStats.data?.data, 'category')[DATA_QUALITY_CATEGORY] ?? 0,
        [categoryStats.data],
    )

    return (
        <div data-testid="data-quality-page">
            <PageHeader
                className="mb-4"
                icon={SECTION_ICONS.dataQuality}
                title={t('data:quality.title')}
                titleAside={
                    !categoryStats.isLoading ? (
                        <span className="text-sm font-normal text-muted-foreground tabular-nums" data-testid="data-quality-total">
                            ({total.toLocaleString()})
                        </span>
                    ) : null
                }
                description={t('data:quality.subtitle')}
            />

            <TicketQueue
                org={org}
                category={DATA_QUALITY_CATEGORY}
                categoryTotal={total}
                state={{
                    subcategory: search.subcategory,
                    status: statusFilter,
                    sort,
                    q: search.q ?? '',
                    page,
                    selected,
                    children: search.children === 1,
                    followUp: search.follow_up,
                }}
                onChange={setSearch}
                onSelect={select}
                testIdPrefix="data-quality"
            />
        </div>
    )
}
